import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslator } from "../../context/translator";
import { useNotifications } from "../../context/Notifications";
import { useSelector } from "react-redux";
import { getCustomerByIdService } from "../../services/customers/services";
import { getCustomerLogsByIdService } from "../../services/logs/services";
import ClientRelatedTo from "../clientRelatedTo/ClientRelatedTo";
import { clientHistory, PERMISSION_CODE } from "../../utils/enum/enums";
import {
  getReferralByClientService,
  saveReferralService,
} from "../../services/referrals/services";
import ReferredFormCategory from "../referredFormCategory/ReferredFormCategory";
import { getCustomerCategoriesService } from "../../services/referredCategories/services";
import Can from "../can/Can";
import { ValidatePermission } from "../../utils/allowHelper/AllowHelper";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";

const getLocalISOString = () => {
  const now = new Date();
  return new Date(
    now.getTime() - now.getTimezoneOffset() * 60000,
  ).toISOString();
};

const ClientReferrals = ({ clientId }) => {
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const { translate } = useTranslator();

  const { user } = useSelector((state) => state.user);
  const [companyAllow, setCompanyAllow] = useState([]);

  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );

  const [isLoading, setIsLoading] = useState(false);

  const [client, setClient] = useState({
    firstName: "",
    lastName: "",
    accountNumber: "",
  });

  const [referrals, setReferrals] = useState([]);
  const [referralBy, setReferralBy] = useState(null);
  const [newReferral, setNewReferral] = useState({
    referredId: null,
    clientId: null,
  });
  const [openCategoryModal, setOpenCategoryModal] = useState(false);
  const [selectedReferred, setSelectedReferred] = useState(null);

  const [histories, setHistories] = useState([]);
  const [categories, setCategories] = useState([]);

  const getCategories = React.useCallback(async () => {
    setIsLoading(true);

    const data = await getCustomerCategoriesService({
      companyId: company?._id,
    });

    setIsLoading(false);

    if (!data || data?.code === PERMISSION_CODE.PERMISSION_DENIED) {
      setCategories([]);
      return;
    }

    setCategories(
      [{ _id: "0", name: "no-selected", key: "000" }, ...data?.categories] ??
        [],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?._id, userProfile?.user]);

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  useEffect(() => {
    if (company && company?.allow) {
      setCompanyAllow({ ...company?.allow });
    }
  }, [company]);

  // ------------------------------
  // LOAD CLIENT
  // ------------------------------
  const getClient = useCallback(async () => {
    if (!clientId || clientId === "new") return;

    const data = await getCustomerByIdService({ customerId: clientId });
    if (data?.customer) {
      setClient(data.customer);
      setNewReferral((prev) => ({
        ...prev,
        clientId: data.customer._id,
      }));
    }
  }, [clientId]);

  useEffect(() => {
    getClient();
  }, [getClient]);

  // ------------------------------
  // LOAD REFERRALS
  // ------------------------------
  const fetchReferral = useCallback(async () => {
    if (!clientId) return;

    const data = await getReferralByClientService({ clientId: clientId });

    const referralsData = data.referrals || [];

    if (referralsData) {
      const referrals = referralsData.filter((r) => r.referredId !== clientId);
      const referralBy = referralsData.find((r) => r.referredId === clientId);

      setReferrals(referrals);
      setReferralBy(referralBy ? referralBy : null);
    }
  }, [clientId]);

  useEffect(() => {
    fetchReferral();
  }, [fetchReferral]);

  // ------------------------------
  // LOAD HISTORIES (LAST 30 DAYS)
  // ------------------------------
  const fetchHistories = useCallback(async () => {
    try {
      setIsLoading(true);

      const { logs = [] } = await getCustomerLogsByIdService({
        customerId: clientId,
        category: clientHistory.DEPOSIT,
        dateFrom: getLocalISOString(),
        dateTo: getLocalISOString(),
        getAll: true,
      });

      setHistories(logs);
    } catch (error) {
      console.error("Error fetching customer logs:", error);
      setHistories([]);
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchHistories();
  }, [fetchHistories]);

  // ------------------------------
  // MEMO: CHECK LAST 30 DAYS DEPOSITS
  // ------------------------------
  const hasRecentDeposits = useMemo(() => {
    if (!histories?.length) return false;

    const now = new Date();
    const days30 = 30 * 24 * 60 * 60 * 1000;
    return histories.some((h) => now - new Date(h.dateTime) <= days30);
  }, [histories]);

  const lastDepositDate = useMemo(() => {
    if (!histories?.length) return null;

    return histories.map((h) => new Date(h.dateTime)).sort((a, b) => b - a)[0];
  }, [histories]);

  // ------------------------------
  // ADD / REMOVE / RESTORE
  // ------------------------------
  const handleRelatedTo = (value) => {
    if (!value) return;
    setNewReferral((prev) => ({ ...prev, ...value }));
  };

  const handleOnChangeReferredByCheck = (e) => {
    const { value, checked } = e.target;
    const val = e.target.type === "checkbox" ? checked : value;

    setReferralBy((prev) => ({ ...prev, verificationReferral: val }));
  };

  const handleOnCategory = (value, id, name) => {
    setReferrals((prev) =>
      prev.map((r) =>
        r._id === id
          ? {
              ...r,
              [name]: value,
            }
          : r,
      ),
    );
    setSelectedReferred((prev) => ({ ...prev, [name]: value }));
  };

  const openCategory = (ref) => {
    setSelectedReferred(ref);
    setOpenCategoryModal(true);
  };

  const handleAddReferral = () => {
    if (!newReferral._id) {
      addNotification({
        id: Date.now(),
        message: translate("select-customer-to-refer"),
        kind: "danger",
      });
      return;
    }

    if (referrals.some((r) => r.referred?._id === newReferral._id)) {
      addNotification({
        id: Date.now(),
        message: translate("customer-already-referred"),
        kind: "danger",
      });
      return;
    }

    setReferrals((prev) => [
      ...prev,
      {
        referredId: newReferral._id,
        clientId,
        referred: newReferral,
        isNew: true,
        remove: false,
      },
    ]);

    setNewReferral({ referredId: null, clientId });
  };

  const handleRemoveReferral = (id) => {
    setReferrals((prev) =>
      prev.map((r) => (r.referred._id === id ? { ...r, remove: true } : r)),
    );
  };

  const handleRestoreReferral = (id) => {
    setReferrals((prev) =>
      prev.map((r) => (r.referred._id === id ? { ...r, remove: false } : r)),
    );
  };

  // ------------------------------
  // SAVE
  // ------------------------------
  const saveReferrals = async () => {
    try {
      setIsLoading(true);

      const response = await saveReferralService({
        referralData: [...referrals, ...(referralBy ? [referralBy] : [])],
        companyId: company?._id,
        userExt: userProfile?.user?.ext,
      });

      if (response?.success) {
        addNotification({
          id: Date.now(),
          message: "Referral updated successfully.",
          kind: "success",
        });
        await fetchReferral();
      } else {
        addNotification({
          id: Date.now(),
          message: translate(response.error) || "Error updating referral.",
          kind: "danger",
        });
      }
    } catch (error) {
      addNotification({
        id: Date.now(),
        message: "Error updating referral.",
        kind: "danger",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const closeCategoryModal = () => {
    if (
      !selectedReferred?.stateReason &&
      selectedReferred?.stateId &&
      selectedReferred?.stateId !== "0"
    ) {
      addNotification({
        id: Date.now(),
        message: translate("should-add-reason-category"),
        kind: "danger",
      });
    } else {
      setOpenCategoryModal(false);
    }
  };

  if (userLoading || companyLoading) return null;

  return (
    <>
      <div className="p-4 bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
              {client.firstName} {client.lastName}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              #{client.accountNumber}
            </p>
          </div>
          <div>
            <p className="font-medium text-gray-800 dark:text-gray-200">
              {translate("deposits-last-30-days")}:{" "}
              {hasRecentDeposits ? (
                <span className="text-green-600 dark:text-green-400 font-semibold">
                  {translate("yes")}
                </span>
              ) : (
                <span className="text-red-600 dark:text-red-400 font-semibold">
                  {translate("no")}
                </span>
              )}
            </p>
            <p className="font-medium text-gray-800 dark:text-gray-200">
              {translate("last-deposit")}:{" "}
              {lastDepositDate?.toLocaleDateString() || "-"}
            </p>
          </div>

          <div>
            <p className="font-medium text-gray-700 dark:text-gray-300">
              {translate("referred-by")}:
            </p>

            {referralBy?.client?._id ? (
              <div className="flex  gap-4">
                <Link
                  onClick={() =>
                    navigate(`/clients/${referralBy?.client?._id}`)
                  }
                  className="mt-2 text-blue-600 dark:text-blue-400 underline text-sm"
                >
                  {referralBy?.client?.fullName} - #
                  {referralBy?.client?.accountNumber}
                </Link>
                <div
                  className={`flex items-center px-2 py-1 rounded ${
                    referralBy?.verificationReferral
                      ? "bg-blue-500"
                      : "bg-slate-900 dark:bg-slate-300"
                  }`}
                >
                  <input
                    name="verificationReferral"
                    type="checkbox"
                    className="mr-2"
                    onChange={handleOnChangeReferredByCheck}
                    checked={!!referralBy?.verificationReferral}
                  />
                  <span
                    className={`text-xs ${
                      referralBy?.verificationReferral
                        ? "text-white"
                        : "text-white dark:text-gray-900 "
                    }`}
                  >
                    {translate("verify-address")}
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                {translate("no-referral")}
              </p>
            )}
          </div>
          <div>
            <button
              onClick={saveReferrals}
              disabled={
                isLoading ||
                (ValidatePermission(userProfile?.user, [
                  PERMISSION_CODE.EDIT_REFERRALS,
                ]) === false &&
                  ValidatePermission(userProfile?.user, [
                    PERMISSION_CODE.CREATE_CUSTOMER,
                  ]) === false)
              }
              className="px-4 py-2 bg-blue-600 text-white rounded-md dark:bg-blue-500 disabled:opacity-50"
            >
              {isLoading ? translate("saving") : translate("save")}
            </button>
          </div>
        </div>
      </div>

      {/* --------------------------------------------------- */}
      {/* REFERRED TO SECTION */}
      {/* --------------------------------------------------- */}
      <div className="mt-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-gray-800 dark:text-gray-200">
            {translate("referred-customers")}
          </h3>

          <div className="flex justify-end items-end gap-4">
            <Can permissions={[PERMISSION_CODE.CREATE_REFERRALS]}>
              <div className="min-w-80">
                {client?._id && (
                  <ClientRelatedTo
                    key={"related-to"}
                    relatedTo={newReferral}
                    customerId={clientId}
                    onChange={handleRelatedTo}
                    label={translate("referred-to")}
                    allow={companyAllow}
                    isMulti={false}
                    referrals={referrals}
                  />
                )}
              </div>
              <div>
                <button
                  onClick={handleAddReferral}
                  disabled={isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md dark:bg-blue-500 disabled:opacity-50"
                >
                  {translate("add-referral")}
                </button>
              </div>
            </Can>
          </div>
        </div>

        {/* --------------------------------------------------- */}
        {/* ACTIVE REFERRALS */}
        {/* --------------------------------------------------- */}
        {referrals.filter((r) => !r.remove).length > 0 ? (
          <table className="min-w-full text-sm text-left border dark:border-slate-700">
            <thead className="bg-gray-100 dark:bg-slate-700">
              <tr>
                <th className="p-2 text-gray-700 dark:text-gray-300">
                  {translate("account-number")}
                </th>
                <th className="p-2 text-gray-700 dark:text-gray-300">
                  {translate("name")}
                </th>
                <th className="p-2 text-gray-700 dark:text-gray-300">
                  {translate("status")}
                </th>
                <th className="p-2 text-gray-700 dark:text-gray-300">
                  {translate("date-referred")}
                </th>
                <th className="p-2 text-gray-700 dark:text-gray-300">
                  {translate("actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {referrals
                .filter((c) => !c.remove)
                .map((c) => (
                  <tr
                    key={c.referred._id}
                    className="border-t dark:border-slate-700"
                  >
                    <td className="p-2 text-gray-700 dark:text-gray-300">
                      {c.referred.accountNumber}
                    </td>
                    <td className="p-2 text-gray-700 dark:text-gray-300">
                      {c.referred.firstName} {c.referred.lastName}
                    </td>
                    <td className="p-2 text-gray-700 dark:text-gray-300">
                      {(categories.find((cat) => cat._id === c.stateId)?.name &&
                        translate(
                          categories.find((cat) => cat._id === c.stateId)?.name,
                        )) ||
                        "-"}
                    </td>
                    <td className="p-2 text-gray-700 dark:text-gray-300">
                      {c?.referralDate
                        ? new Date(c?.referralDate).toLocaleDateString()
                        : "-"}
                    </td>
                    <td className="flex p-2 gap-4 items-center justify-start">
                      <Can permissions={[PERMISSION_CODE.EDIT_REFERRALS]}>
                        <button
                          onClick={() => openCategory(c)}
                          className="text-blue-600 dark:text-blue-400 underline"
                        >
                          {translate("edit-status")}
                        </button>
                      </Can>

                      <button
                        onClick={() => navigate(`/clients/${c.referred._id}`)}
                        className="text-blue-600 dark:text-blue-400 underline"
                      >
                        {translate("view")}
                      </button>

                      <Can permissions={[PERMISSION_CODE.EDIT_REFERRALS]}>
                        <button
                          onClick={() => handleRemoveReferral(c.referred._id)}
                          className="text-red-600 dark:text-red-400 underline"
                        >
                          {translate("remove")}
                        </button>
                      </Can>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        ) : (
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            {translate("no-referral-customers")}
          </p>
        )}

        {/* --------------------------------------------------- */}
        {/* REMOVED REFERRALS */}
        {/* --------------------------------------------------- */}
        {referrals.filter((r) => r.remove).length > 0 && (
          <div className="mt-10">
            <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">
              {translate("removed-referrals")}
            </h3>

            <table className="min-w-full text-sm text-left border dark:border-slate-700">
              <thead className="bg-red-100 dark:bg-slate-800">
                <tr>
                  <th className="p-2 text-gray-700 dark:text-gray-300">
                    {translate("account-number")}
                  </th>
                  <th className="p-2 text-gray-700 dark:text-gray-300">
                    {translate("name")}
                  </th>
                  <th className="p-2 text-gray-700 dark:text-gray-300">
                    {translate("actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {referrals
                  .filter((c) => c.remove)
                  .map((c) => (
                    <tr
                      key={c.referred._id}
                      className="border-t dark:border-slate-700 opacity-70"
                    >
                      <td className="p-2 text-gray-700 dark:text-gray-300">
                        {c.referred.accountNumber}
                      </td>
                      <td className="p-2 text-gray-700 dark:text-gray-300">
                        {c.referred.firstName} {c.referred.lastName}
                      </td>
                      <td className="flex p-2">
                        <button
                          onClick={() => handleRestoreReferral(c.referred._id)}
                          className="text-green-600 dark:text-green-400 underline"
                        >
                          {translate("restore")}
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        {openCategoryModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl p-6 w-96">
              <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-100">
                {translate("edit-status")}
              </h3>

              <ReferredFormCategory
                key={selectedReferred._id}
                translate={translate}
                onChange={handleOnCategory}
                referred={selectedReferred}
                categories={categories}
              />

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={closeCategoryModal}
                  className="px-4 py-2 bg-gray-300 dark:bg-slate-700 text-gray-900 dark:text-white rounded"
                >
                  {translate("close")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ClientReferrals;
